import { useEffect, useMemo, useReducer, useState } from "react"
import axios, { CancelToken } from "axios"
import { ApiAccess } from 'features/session/types'
import { useApiAccess } from "features/session/context"

export type FetchState<T> = {
  loading: boolean
  error: Error | null
  data: T | null
  refetch: () => void
}
type FetchAction<T> = {
  type: 'clear'
} | {
  type: 'start'
} | {
  type: 'success'
  payload: T
} | {
  type: 'error'
  error: Error
} | {
  type: 'finish'
}

const fetchReducer = <T>(
  state: FetchState<T>,
  action: FetchAction<T>
): FetchState<T> => {
  switch (action.type) {
    case 'clear':
      return { ...state, loading: false, error: null, data: null }
    case 'start':
      return { ...state, loading: true, error: null }
    case 'error':
      return { ...state, loading: false, error: action.error }
    case 'success':
      return { ...state, data: action.payload, error: null }
    case 'finish':
      return { ...state, loading: false }
  }
}

export const useApiFetch = <T>(
  fetchFunc: (
    apiAccess: ApiAccess,
    cancelToken: CancelToken,
    fetchTime?: number
  ) => Promise<T | null> | T | null | undefined,
  deps?: React.DependencyList
): FetchState<T> => {
  const [fetchTime, setFetchTime] = useState<number | undefined>(undefined)
  const refetch = () => setFetchTime((new Date()).getTime())
  const [state, dispatch] = useReducer(fetchReducer, {
    loading: false,
    error: null,
    data: null,
    loaded: null,
    total: null,
    refetch
  } as FetchState<T>)

  const cancel = axios.CancelToken.source()
  const apiAccess = useApiAccess()
  useEffect(
    () => {
      dispatch({ type: 'clear' })

      let ignore = false
      const promise = fetchFunc(apiAccess, cancel.token, fetchTime)
      if (!promise) return
      if (promise instanceof Promise) {
        promise
          .then((response) => {
            if (!ignore && response) dispatch({ type: 'success', payload: response })
          })
          .catch((error) => {
            if (axios.isCancel(error)) {
              console.log('request canceled')
            } else if (!ignore) {
              dispatch({ type: 'error', error: error })
            }
          })
          .finally(() => {
            dispatch({ type: 'finish' })
          })
        dispatch({ type: 'start' })
      } else {
        dispatch({ type: 'success', payload: promise })
      }
      return () => {
        ignore = true
        if (state.loading) cancel.cancel('canceled')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([fetchTime, fetchFunc, dispatch/*, cancel, state.loading*/])
  )
  return useMemo(
    () => state as FetchState<T>,
    [state]
  )
}

export const useFetch = <T>(
  fetchFunc: (
    cancelToken: CancelToken
  ) => Promise<T | null> | T | null | undefined,
  deps?: React.DependencyList
): FetchState<T> => {
  const [fetchTime, setFetchTime] = useState<Date>(() => new Date())
  const refetch = () => setFetchTime(new Date())
  const [state, dispatch] = useReducer(fetchReducer, {
    loading: false,
    error: null,
    data: null,
    loaded: null,
    total: null,
    refetch
  } as FetchState<T>)

  const cancel = axios.CancelToken.source()
  useEffect(
    () => {
      //dispatch({ type: 'clear' })

      let ignore = false
      const promise = fetchFunc(cancel.token)
      if (!promise) return
      if (promise instanceof Promise) {
        promise
          .then((response) => {
            if (!ignore && response) dispatch({ type: 'success', payload: response })
          })
          .catch((error) => {
            if (axios.isCancel(error)) {
              console.log('request canceled')
            } else if (!ignore) {
              dispatch({ type: 'error', error: error })
            }
          })
          .finally(() => {
            dispatch({ type: 'finish' })
          })
        dispatch({ type: 'start' })
      } else {
        dispatch({ type: 'success', payload: promise })
      }
      return () => {
        ignore = true
        if (state.loading) cancel.cancel('canceled')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    (deps ?? []).concat([fetchTime, fetchFunc, dispatch/*, cancel, state.loading*/])
  )
  return useMemo(
    () => state as FetchState<T>,
    [state]
  )
}
